import api from "@/lib/api";

// Meta standard events use fbq('track', ...); everything else is a custom event.
const STANDARD = new Set([
  "PageView", "ViewContent", "Lead", "CompleteRegistration",
  "StartTrial", "Purchase", "Subscribe",
]);

// Only genuine marketing-funnel conversions are allowed to reach Meta. These fire even
// at the signup moment (when the user has just authenticated); everything else is gated off.
const CONVERSION_EVENTS = new Set([
  "Lead", "StartTrial", "CompleteRegistration", "Purchase", "Subscribe",
]);

// Production hosts (marketing site + app). Preview/staging/localhost never send to Meta.
const ALLOWED_HOSTS = ["recoveros.in", "www.recoveros.in", "app.recoveros.in"];

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function hostAllowed() {
  try { return ALLOWED_HOSTS.includes(window.location.hostname); } catch (e) { return false; }
}

function currentUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch (e) { return null; }
}

function isAuthenticated() {
  try { return !!localStorage.getItem("auth_token") || !!currentUser(); } catch (e) { return false; }
}

// Super admins, RecoverOS staff (@recoveros.in), developers and demo accounts are never tracked.
function isInternalUser() {
  const u = currentUser();
  if (!u) return false;
  const role = (u.role || "").toLowerCase();
  const email = (u.email || "").toLowerCase();
  if (role === "super_admin" || role === "developer") return true;
  if (u.is_demo || u.is_internal) return true;
  if (email.endsWith("@recoveros.in")) return true;
  return false;
}

// Central gate: env (host) + internal-user exclusion + auth-state gate (only conversions survive login).
export function metaAllowed(eventName) {
  if (!hostAllowed()) return false;
  if (isInternalUser()) return false;
  if (!CONVERSION_EVENTS.has(eventName) && isAuthenticated()) return false;
  return true;
}

// Persist Meta click id (fbclid) as a first-party _fbc cookie so CAPI can match ad clicks.
export function persistFbclid() {
  try {
    const u = new URL(window.location.href);
    const fbclid = u.searchParams.get("fbclid");
    if (fbclid && !getCookie("_fbc")) {
      const v = `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`;
      document.cookie = `_fbc=${v}; path=/; max-age=${60 * 60 * 24 * 90}; SameSite=Lax`;
    }
  } catch (e) { /* noop */ }
}

function newEventId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* noop */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Fires the browser Pixel event AND a deduplicated server-side CAPI event (shared event_id).
// Both are suppressed together by metaAllowed() so browser and server stay consistent.
export function trackMeta(eventName, { customData = {}, email = null, phone = null } = {}) {
  if (!metaAllowed(eventName)) return null;

  const eventId = newEventId();
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");
  const hasPixel = typeof window !== "undefined" && !!window.fbq;

  try {
    if (hasPixel) {
      const method = STANDARD.has(eventName) ? "track" : "trackCustom";
      window.fbq(method, eventName, customData, { eventID: eventId });
    }
  } catch (e) { /* noop */ }

  try {
    api.post("/meta/track", {
      event_name: eventName,
      event_id: eventId,
      event_source_url: typeof window !== "undefined" ? window.location.href : null,
      client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      user_data: { email, phone, fbp, fbc },
      custom_data: customData,
      pixel_fired: hasPixel,
    }).catch(() => {});
  } catch (e) { /* noop */ }

  return eventId;
}
